import { useEffect, useState } from 'react';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { findUtType, utUnitOf } from 'pages/ut/constants';
import { MECH_ITEM_GROUP_CD } from 'pages/ut/report/utItemCodes';
import { UtType } from 'pages/ut/types';

/**
 * 기계 UT 항목 목록 — **표시 목록·순서·명칭만** 공통코드(MECH_UT_CTGR_CD)에서 가져온다.
 *
 * 값 계산은 여전히 constants.ts에 묶여 있다. `UtKey`가 리터럴 유니온이고
 * `UT_COLUMN_MAP`이 UT키 → TB_EELMB_UTM_D 컬럼을 하드코딩하고 있어서, 공통코드에만 있는
 * 코드는 사용량을 읽어올 방법이 없다. 그래서 **UT_TYPES에 매칭되는 코드만** 통과시킨다
 * (findUtType이 'ST'/'Steam' 양쪽 표기를 받아준다).
 *
 * 라벨은 공통코드명 + 기존 단위로 만든다 — `CDA` + `Nl/min` → `CDA [Nl/min]`.
 * 단위는 공통코드에 없어서 UT_TYPES의 label이 유일한 출처다.
 *
 * ⚠ 공통코드가 비거나 조회에 실패하면 **빈 목록**을 돌려준다(화면도 빈다).
 *   분석 Report 탭과 같은 규칙이다 — 저장된 데이터로 목록을 대신 만들지 않는다.
 */

/** 화면 여러 곳에서 쓰므로 조회는 한 번만 하고 결과를 공유한다 */
let cachedPromise: Promise<UtType[]> | null = null;

const fetchMechUtTypes = (): Promise<UtType[]> => {
  if (!cachedPromise) {
    cachedPromise = getCommonCodeNames(MECH_ITEM_GROUP_CD)
      .then((codes: Code[] | null) =>
        (codes ?? [])
          .map((c) => {
            const cd = String(c?.cmnCd ?? '');
            const ut = findUtType(cd);
            if (!ut) return null;
            const unit = utUnitOf(cd);
            const nm = c?.cmnCdNm || ut.key;
            return { key: ut.key, label: unit ? `${nm} [${unit}]` : nm } as UtType;
          })
          .filter((t): t is UtType => !!t)
      )
      .catch(() => [] as UtType[]);
  }
  return cachedPromise;
};

export const useMechUtTypes = (): UtType[] => {
  const [types, setTypes] = useState<UtType[]>([]);

  useEffect(() => {
    let alive = true;
    fetchMechUtTypes().then((list) => {
      if (alive) setTypes(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  return types;
};
